// useHospitalCodeCatalog — every billing code THIS hospital publishes (CPT,
// HCPCS, MS-DRG, REV) with its description and listed charge, from the
// per-hospital static shards the Services tab already uses. Loaded on
// demand (≈1.2 MB for a large hospital) the first time the City view's
// filter box is opened, then cached for the session.
import { useQuery } from "@tanstack/react-query";
import { loadStaticData } from "@/lib/offlineData";
import type { CodeFamily } from "@/hooks/useCodeNeighbourPrices";

export interface HospitalCodeRow {
  fam: CodeFamily;
  code: string;
  desc: string;
  here: number | null;      // the shard's listed charge (hc)
  national: number | null;  // the shard's national average (na)
}

interface ShardRow { c?: string | number; d?: string; hc?: number | string | null; na?: number | string | null }
const FILES: [CodeFamily, string][] = [["cpt", "cpt"], ["hcpcs", "hcpcs"], ["drg", "drg"], ["rev", "revenue"]];
const num = (v: unknown): number | null => { if (v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

export function useHospitalCodeCatalog(hospitalId: string | undefined, enabled: boolean) {
  return useQuery<HospitalCodeRow[]>({
    queryKey: ["hospital-code-catalog", hospitalId],
    enabled: !!hospitalId && enabled,
    staleTime: Infinity,
    queryFn: async () => {
      const out: HospitalCodeRow[] = [];
      await Promise.all(FILES.map(async ([fam, file]) => {
        let rows: ShardRow[] | null = null;
        try { rows = await loadStaticData<ShardRow[]>(`hospitals/${hospitalId}/${file}.json`); } catch { rows = null; }
        if (!Array.isArray(rows)) return;
        const seen = new Set<string>();
        for (const r of rows) {
          const code = r?.c != null ? String(r.c) : "";
          if (!code || seen.has(code)) continue;
          seen.add(code);
          out.push({ fam, code, desc: r.d || "", here: num(r.hc), national: num(r.na) });
        }
      }));
      return out;
    },
  });
}
